import { Bot, MessageCircle, PhoneCall, User } from "lucide-react";
import { useGetList } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { eventTime } from "../home/homeKpis";
import {
  firstQuestion,
  pageLabel,
  sourceLabel,
  type WebChat,
} from "./webStats";

/** Chatbot conversations of the web, newest first; click to read one. */
export const WebChats = () => {
  const [perPage, setPerPage] = useState(25);
  const [open, setOpen] = useState<WebChat | null>(null);
  const { data, total, isPending } = useGetList<WebChat>(
    "gula_web_chats_summary",
    {
      pagination: { page: 1, perPage },
      sort: { field: "updated_at", order: "DESC" },
    },
    { refetchInterval: 60_000 },
  );

  if (isPending) {
    return (
      <p className="text-sm text-muted-foreground">Cargando conversaciones…</p>
    );
  }
  if (!data?.length) {
    return (
      <p className="text-sm text-muted-foreground">
        Todavía no hay conversaciones del chat. Saldrán aquí en cuanto alguien
        le escriba a la IA de la web.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="divide-y rounded-2xl border">
        {data.map((chat) => (
          <li key={chat.id}>
            <button
              type="button"
              onClick={() => setOpen(chat)}
              className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/50"
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <MessageCircle className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">
                  {firstQuestion(chat.messages) || "(sin pregunta)"}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {eventTime(chat.updated_at)} · {chat.message_count}{" "}
                  {chat.message_count === 1 ? "pregunta" : "preguntas"} ·{" "}
                  {pageLabel(chat.page)} ·{" "}
                  {sourceLabel(
                    chat.utm_source ?? (chat.fbclid ? "facebook" : null),
                  )}
                  {chat.utm_content ? ` · ${chat.utm_content}` : ""}
                </span>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-1">
                {chat.lead ? (
                  <Badge className="bg-lime-300 text-neutral-900 hover:bg-lime-300">
                    Dejó su teléfono
                  </Badge>
                ) : chat.callback_offered ? (
                  <Badge variant="outline">Le ofreció llamada</Badge>
                ) : null}
                {chat.client_name ? (
                  <span className="text-xs text-muted-foreground">
                    {chat.client_name}
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {total && total > data.length ? (
        <Button
          variant="outline"
          className="self-center"
          onClick={() => setPerPage((n) => n + 25)}
        >
          Ver más ({total - data.length})
        </Button>
      ) : null}
      <ChatDialog chat={open} onClose={() => setOpen(null)} />
    </div>
  );
};

const ChatDialog = ({
  chat,
  onClose,
}: {
  chat: WebChat | null;
  onClose: () => void;
}) => (
  <Dialog open={!!chat} onOpenChange={(value) => !value && onClose()}>
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
      {chat ? (
        <>
          <DialogHeader>
            <DialogTitle>Conversación con la IA de la web</DialogTitle>
            <DialogDescription>
              {eventTime(chat.started_at)} · empezó en {pageLabel(chat.page)} ·{" "}
              {chat.device === "mobile" ? "móvil" : (chat.device ?? "?")} ·{" "}
              {sourceLabel(
                chat.utm_source ?? (chat.fbclid ? "facebook" : null),
              )}
              {chat.utm_campaign ? ` · ${chat.utm_campaign}` : ""}
            </DialogDescription>
          </DialogHeader>
          {chat.client_id ? (
            <Link
              to={`/clientes?cliente=${chat.client_id}`}
              className="flex items-center gap-2 rounded-2xl border border-lime-300 bg-lime-100 px-3 py-2 text-sm font-medium text-neutral-900 dark:border-lime-300/40 dark:bg-lime-300/10 dark:text-lime-100"
            >
              <PhoneCall className="h-4 w-4" />
              Dejó su teléfono: {chat.client_name ?? "abrir ficha"} → ver ficha
            </Link>
          ) : chat.lead ? (
            <p className="rounded-2xl border px-3 py-2 text-sm">
              Dejó su teléfono en «Que me llamen» (aún no aparece en el CRM).
            </p>
          ) : null}
          <div className="flex flex-col gap-3">
            {chat.messages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  "flex gap-2",
                  message.role === "user" ? "flex-row-reverse" : "",
                )}
              >
                <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  {message.role === "user" ? (
                    <User className="h-3.5 w-3.5" />
                  ) : (
                    <Bot className="h-3.5 w-3.5" />
                  )}
                </span>
                <div
                  className={cn(
                    "max-w-[80%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm",
                    message.role === "user"
                      ? "bg-[#082130] text-white dark:bg-sky-900"
                      : "bg-muted",
                  )}
                >
                  {message.content}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </DialogContent>
  </Dialog>
);
